import { useState, useEffect, useCallback, useMemo } from "react";
import api, { apiError } from "../../lib/api";
import { Card, Loading, inputCls, labelCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import { Plus, RefreshCw, Trash2, ArrowLeft, Loader2, ExternalLink, Phone, Reply, Copy, Upload, Send, MousePointerClick, Search } from "lucide-react";
import { toast } from "sonner";

const TPL_STATUS = {
  APPROVED: "bg-emerald-100 text-emerald-700", PENDING: "bg-amber-100 text-amber-700", IN_APPEAL: "bg-amber-100 text-amber-700",
  REJECTED: "bg-rose-100 text-rose-700", PAUSED: "bg-slate-200 text-slate-700", DISABLED: "bg-slate-200 text-slate-700",
};
const LANGS = [["en", "English"], ["en_US", "English (US)"], ["hi", "Hindi"], ["mr", "Marathi"], ["gu", "Gujarati"]];
const BTN_TYPES = [["QUICK_REPLY", "Quick reply"], ["URL", "Visit website"], ["PHONE_NUMBER", "Call phone number"], ["COPY_CODE", "Copy offer code"]];

function compText(t, type) { return (t.components || []).find((c) => (c.type || "").toUpperCase() === type) || null; }

function TemplateList({ onCreate }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    setRows(null);
    try { const { data } = await api.get("/whatsapp/templates/all"); setRows(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (name) => {
    const typed = window.prompt(`This permanently deletes "${name}" from your Meta WhatsApp account.\nType the template name to confirm:`);
    if (typed !== name) { if (typed !== null) toast.error("Name did not match — not deleted"); return; }
    try { await api.delete(`/whatsapp/templates/${name}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const shown = useMemo(() => (rows || []).filter((t) => (filter === "ALL" || t.status === filter) && t.name.includes(q.toLowerCase())), [rows, filter, q]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input className={`${inputCls} pl-9`} placeholder="Search templates" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tpl-search-input" />
        </div>
        <select className={inputCls.replace("w-full", "w-44")} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="tpl-status-filter">
          {["ALL", "APPROVED", "PENDING", "REJECTED", "PAUSED"].map((s) => <option key={s} value={s}>{s === "ALL" ? "All statuses" : s}</option>)}
        </select>
        <button className={`${btnSecondary} px-3`} onClick={load} data-testid="tpl-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
        <button className={btnPrimary} onClick={onCreate} data-testid="create-template-btn"><Plus className="w-4 h-4" /> Create Template</button>
      </div>
      {rows === null ? <Loading /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr><th className="px-4 py-2.5 text-left">Name</th><th className="px-4 py-2.5 text-left">Category</th><th className="px-4 py-2.5 text-left">Language</th><th className="px-4 py-2.5 text-left">Status</th><th className="px-4 py-2.5 text-left">Body</th><th /></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shown.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50 align-top" data-testid="tpl-row">
                    <td className="px-4 py-3 font-mono text-xs text-slate-800">{t.name}
                      <div className="flex gap-1 mt-1">
                        {t.created_in_app && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-sans">Created here</span>}
                        {t.tracked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 font-sans flex items-center gap-0.5"><MousePointerClick className="w-3 h-3" />Click tracking</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{t.category}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{t.language}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TPL_STATUS[t.status] || "bg-slate-100 text-slate-600"}`} data-testid="tpl-status">{t.status}</span>
                      {t.status === "REJECTED" && t.rejected_reason && t.rejected_reason !== "NONE" && <div className="text-[11px] text-rose-600 mt-1">{t.rejected_reason}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-sm"><div className="line-clamp-2">{compText(t, "BODY")?.text}</div></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => del(t.name)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`tpl-delete-${t.name}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {shown.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400 text-sm">No templates found.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function varCount(text) { const m = (text || "").match(/\{\{\s*(\d+)\s*\}\}/g) || []; return new Set(m.map((x) => x.replace(/\D/g, ""))).size; }

function Preview({ f }) {
  const fill = (text, ex) => (text || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => ex[n - 1] || `{{${n}}}`);
  const auth = f.category === "AUTHENTICATION";
  return (
    <div className="rounded-2xl bg-[#e5ddd5] p-4 min-h-[300px]" data-testid="tpl-preview">
      <div className="bg-white rounded-lg shadow-sm max-w-[300px] overflow-hidden">
        {!auth && f.header_format === "TEXT" && f.header_text && <div className="px-3 pt-2.5 font-semibold text-sm text-slate-900">{fill(f.header_text, [f.header_example])}</div>}
        {!auth && ["IMAGE", "VIDEO", "DOCUMENT"].includes(f.header_format) && <div className="h-32 bg-slate-200 flex items-center justify-center text-xs text-slate-500">{f.header_media_name || f.header_format}</div>}
        <div className="px-3 py-2 text-[13px] text-slate-800 whitespace-pre-wrap">{auth ? "<code> is your verification code. For your security, do not share this code." : (fill(f.body, f.body_examples) || "Message body…")}</div>
        {!auth && f.footer && <div className="px-3 pb-2 text-[11px] text-slate-400">{f.footer}</div>}
        <div className="px-3 pb-1 text-right text-[10px] text-slate-400">12:00</div>
        {(auth ? [{ type: "COPY_CODE", text: "Copy code" }] : [...f.buttons].sort((a, b) => (a.type === "QUICK_REPLY" ? 0 : 1) - (b.type === "QUICK_REPLY" ? 0 : 1))).map((b, i) => (
          <div key={i} className="border-t border-slate-100 py-2 text-center text-[13px] text-sky-600 flex items-center justify-center gap-1.5">
            {b.type === "URL" && <ExternalLink className="w-3.5 h-3.5" />}{b.type === "PHONE_NUMBER" && <Phone className="w-3.5 h-3.5" />}
            {b.type === "QUICK_REPLY" && <Reply className="w-3.5 h-3.5" />}{b.type === "COPY_CODE" && <Copy className="w-3.5 h-3.5" />}
            {b.type === "COPY_CODE" ? (auth ? "Copy code" : "Copy offer code") : (b.text || "Button")}
          </div>
        ))}
      </div>
    </div>
  );
}

function ButtonEditor({ b, i, set, remove }) {
  const up = (k, v) => set(i, { ...b, [k]: v });
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-2 bg-slate-50/50" data-testid={`tpl-button-${i}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-slate-600 flex-1">{BTN_TYPES.find((x) => x[0] === b.type)?.[1]}</span>
        <button onClick={() => remove(i)} className="text-slate-400 hover:text-rose-600" data-testid={`tpl-button-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {b.type !== "COPY_CODE" && <input className={inputCls} maxLength={25} placeholder="Button label (max 25)" value={b.text} onChange={(e) => up("text", e.target.value)} data-testid={`tpl-button-text-${i}`} />}
      {b.type === "URL" && (
        <>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={b.track} onChange={(e) => up("track", e.target.checked)} data-testid={`tpl-button-track-${i}`} />
            Track clicks (link goes through GDIOT PAY so every tap is counted)
          </label>
          {b.track ? (
            <input className={inputCls} placeholder="Destination link https://… (required; you can also map a different link per send)" value={b.destination} onChange={(e) => up("destination", e.target.value)} data-testid={`tpl-button-destination-${i}`} />
          ) : (
            <>
              <input className={inputCls} placeholder="https://example.com/page  or  https://example.com/{{1}}" value={b.url} onChange={(e) => up("url", e.target.value)} data-testid={`tpl-button-url-${i}`} />
              {b.url.includes("{{") && <input className={inputCls} placeholder="Sample full URL, e.g. https://example.com/abc" value={b.url_example} onChange={(e) => up("url_example", e.target.value)} data-testid={`tpl-button-url-example-${i}`} />}
            </>
          )}
        </>
      )}
      {b.type === "PHONE_NUMBER" && <input className={inputCls} placeholder="Phone number e.g. 9021824051" value={b.phone_number} onChange={(e) => up("phone_number", e.target.value)} data-testid={`tpl-button-phone-${i}`} />}
      {b.type === "COPY_CODE" && <input className={inputCls} maxLength={15} placeholder="Sample offer code" value={b.example} onChange={(e) => up("example", e.target.value)} data-testid={`tpl-button-code-${i}`} />}
    </div>
  );
}

const EMPTY = { name: "", category: "UTILITY", language: "en", header_format: "NONE", header_text: "", header_example: "", header_media_id: null, header_media_name: "", body: "", body_examples: [], footer: "", buttons: [], code_expiration_minutes: 10 };

function CreateTemplate({ onBack }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const n = varCount(f.body);
  const auth = f.category === "AUTHENTICATION";
  const addBtn = (type) => {
    if (f.buttons.length >= 10) return toast.error("Maximum 10 buttons");
    if (type === "URL" && f.buttons.filter((b) => b.type === "URL").length >= 2) return toast.error("Maximum 2 website buttons");
    if (type === "PHONE_NUMBER" && f.buttons.some((b) => b.type === "PHONE_NUMBER")) return toast.error("Only 1 phone button allowed");
    if (type === "COPY_CODE" && f.buttons.some((b) => b.type === "COPY_CODE")) return toast.error("Only 1 copy-code button allowed");
    set("buttons", [...f.buttons, { type, text: "", url: "", url_example: "", track: type === "URL", destination: "", phone_number: "", example: "" }]);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/whatsapp/templates/media?kind=${f.header_format}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, header_media_id: data.media_id, header_media_name: data.filename }));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setUploading(false);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/templates/create", { ...f, body_examples: f.body_examples.slice(0, n), base_url: window.location.origin });
      toast.success(`Submitted to Meta — status: ${data.status}. Approved templates appear automatically in Bulk & Reminders.`);
      onBack();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className={`${btnSecondary} text-sm`} data-testid="tpl-create-back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
        <h2 className="text-lg font-semibold text-slate-900">Create WhatsApp Template</h2>
      </div>
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <Card className="p-5 space-y-5">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3"><label className={labelCls}>Template name</label>
              <input className={`${inputCls} font-mono`} placeholder="e.g. workshop_batch_update" value={f.name} onChange={(e) => set("name", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))} data-testid="tpl-name-input" />
              <p className="text-[11px] text-slate-400 mt-1">Lowercase letters, numbers and underscores only.</p></div>
            <div><label className={labelCls}>Category</label>
              <select className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="tpl-category-select">
                <option value="UTILITY">Utility</option><option value="MARKETING">Marketing</option><option value="AUTHENTICATION">Authentication</option>
              </select></div>
            <div><label className={labelCls}>Language</label>
              <select className={inputCls} value={f.language} onChange={(e) => set("language", e.target.value)} data-testid="tpl-language-select">
                {LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
              </select></div>
          </div>
          {auth ? (
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-xs text-blue-800 space-y-2">
              <p>Authentication templates use Meta's fixed one-time-password text and a "Copy code" button.</p>
              <label className="flex items-center gap-2">Code expires in
                <input type="number" min={1} max={90} className={`${inputCls} w-20 py-1`} value={f.code_expiration_minutes} onChange={(e) => set("code_expiration_minutes", Number(e.target.value))} data-testid="tpl-otp-expiry-input" /> minutes</label>
            </div>
          ) : (
            <>
              <div className="space-y-2"><label className={labelCls}>Header (optional)</label>
                <select className={inputCls} value={f.header_format} onChange={(e) => setF((p) => ({ ...p, header_format: e.target.value, header_media_id: null, header_media_name: "" }))} data-testid="tpl-header-format-select">
                  <option value="NONE">None</option><option value="TEXT">Text</option><option value="IMAGE">Image</option><option value="VIDEO">Video</option><option value="DOCUMENT">Document (PDF)</option>
                </select>
                {f.header_format === "TEXT" && <>
                  <input className={inputCls} maxLength={60} placeholder="Header text (max 60, may contain {{1}})" value={f.header_text} onChange={(e) => set("header_text", e.target.value)} data-testid="tpl-header-text-input" />
                  {f.header_text.includes("{{1}}") && <input className={inputCls} placeholder="Sample value for {{1}}" value={f.header_example} onChange={(e) => set("header_example", e.target.value)} data-testid="tpl-header-example-input" />}
                </>}
                {["IMAGE", "VIDEO", "DOCUMENT"].includes(f.header_format) && (
                  <label className={`${btnSecondary} w-fit text-sm ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="tpl-header-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {f.header_media_name || `Upload ${f.header_format.toLowerCase()}`}
                    <input type="file" className="hidden" accept={{ IMAGE: "image/jpeg,image/png", VIDEO: "video/mp4", DOCUMENT: "application/pdf" }[f.header_format]} onChange={upload} />
                  </label>
                )}
              </div>
              <div className="space-y-2"><label className={labelCls}>Body</label>
                <textarea rows={6} maxLength={1024} className={inputCls} placeholder={"Hello {{1}}, your batch for {{2}} starts on {{3}}."} value={f.body} onChange={(e) => set("body", e.target.value)} data-testid="tpl-body-input" />
                <div className="flex items-center justify-between text-[11px] text-slate-400">
                  <button className="text-blue-600 font-medium" onClick={() => set("body", `${f.body}{{${n + 1}}}`)} data-testid="tpl-add-variable-btn">+ Add variable {`{{${n + 1}}}`}</button>
                  <span>{f.body.length}/1024</span>
                </div>
                {n > 0 && <div className="grid sm:grid-cols-2 gap-2">
                  {Array.from({ length: n }).map((_, i) => (
                    <input key={i} className={inputCls} placeholder={`Sample for {{${i + 1}}}`} value={f.body_examples[i] || ""} data-testid={`tpl-body-example-${i + 1}`}
                      onChange={(e) => { const ex = [...f.body_examples]; ex[i] = e.target.value; set("body_examples", ex); }} />
                  ))}
                </div>}
              </div>
              <div><label className={labelCls}>Footer (optional)</label>
                <input className={inputCls} maxLength={60} placeholder="e.g. Team GDIOT" value={f.footer} onChange={(e) => set("footer", e.target.value)} data-testid="tpl-footer-input" /></div>
              <div className="space-y-2"><label className={labelCls}>Buttons (optional)</label>
                <div className="flex flex-wrap gap-2">
                  {BTN_TYPES.map(([t, l]) => <button key={t} className={`${btnSecondary} text-xs py-1.5 px-3`} onClick={() => addBtn(t)} data-testid={`tpl-add-button-${t}`}><Plus className="w-3.5 h-3.5" />{l}</button>)}
                </div>
                {f.buttons.map((b, i) => <ButtonEditor key={i} b={b} i={i} set={(idx, v) => set("buttons", f.buttons.map((x, j) => (j === idx ? v : x)))} remove={(idx) => set("buttons", f.buttons.filter((_, j) => j !== idx))} />)}
              </div>
            </>
          )}
          <button className={`${btnPrimary} w-full`} disabled={busy || !f.name || (!auth && !f.body)} onClick={submit} data-testid="tpl-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit to Meta for approval
          </button>
          <p className="text-[11px] text-slate-400 text-center">Meta reviews every template (usually a few minutes). Status updates automatically in the Templates list.</p>
        </Card>
        <div className="lg:sticky lg:top-24 space-y-2"><div className={labelCls}>Preview</div><Preview f={f} /></div>
      </div>
    </div>
  );
}

export function WaTemplates() {
  const [mode, setMode] = useState("list");
  return mode === "create" ? <CreateTemplate onBack={() => setMode("list")} /> : <TemplateList onCreate={() => setMode("create")} />;
}
